import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Search, Sun, Moon, Plus, Activity } from 'lucide-react';
import { useTheme } from '../lib/theme.jsx';
import { useAuth } from '../lib/auth.jsx';

/**
 * Sticky top bar inside the main content area — breadcrumb, global search,
 * activity, clock, theme toggle. It sits in the content column rather than
 * pinned across the viewport, so the sidebar keeps its own header.
 */
const LABELS = {
  dashboard: 'Overview',
  payments: 'Payments',
  transactions: 'Transactions',
  'payment-links': 'Payment links',
  customers: 'Customers',
  providers: 'Payment providers',
  developers: 'Developers',
  'api-keys': 'API keys',
  webhooks: 'Webhooks',
  settings: 'Settings',
  profile: 'Business profile',
  security: 'Security',
  admin: 'Admin',
  merchants: 'Merchants',
  health: 'Health',
};

const PARENTS = { developers: null, settings: null };

function buildTrail(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const trail = [{ label: 'Home', href: '/dashboard' }];

  parts.forEach((part, i) => {
    const href = `/${parts.slice(0, i + 1).join('/')}`;
    // A payment id is opaque; show it truncated rather than as a crumb nobody reads.
    const label = LABELS[part] ?? (part.includes('_') ? `${part.slice(0, 10)}…` : part);
    if (part === 'dashboard' && i === 0) return;
    trail.push({ label, href, isSegment: Object.hasOwn(PARENTS, part) });
  });

  return trail;
}

function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <form
      className="relative hidden sm:block"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) navigate(`/transactions?search=${encodeURIComponent(value.trim())}`);
      }}
    >
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-faint" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search"
        aria-label="Search payments"
        className="h-[34px] w-44 rounded-md border bg-surface pl-8 pr-12 text-sm text-ink outline-none transition-colors placeholder:text-faint hover:border-slate-300 focus:border-accent lg:w-60"
        style={{ borderColor: 'rgb(var(--line))' }}
      />
      <kbd
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border px-1.5 py-0.5 text-[10px] font-medium text-faint"
        style={{ borderColor: 'rgb(var(--line))' }}
      >
        ⌘K
      </kbd>
    </form>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Tick on the minute rather than the second — a seconds display in chrome
    // is motion nobody asked for.
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="hidden h-[34px] items-center gap-2 rounded-md border px-2.5 xl:flex"
      style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
    >
      <span className="tabular text-sm font-medium text-ink">
        {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
      </span>
      <span className="text-xs text-muted">
        {now.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
      </span>
    </div>
  );
}

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="grid h-[34px] w-[34px] place-items-center rounded-md border text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink"
      style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function TopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isPlatformAdmin } = useAuth();
  const home = isPlatformAdmin ? '/admin' : '/dashboard';
  const trail = buildTrail(pathname, home);

  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <nav className="mr-2 flex min-w-0 flex-1 items-center gap-1.5 text-sm text-muted" aria-label="Breadcrumb">
        {trail.map((crumb, i) => (
          <span key={`${i}-${crumb.href}`} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden="true" />}
            {i === trail.length - 1 || crumb.isSegment ? (
              <span className={`truncate ${i === trail.length - 1 ? 'font-medium text-ink' : ''}`}>
                {crumb.label}
              </span>
            ) : (
              <Link to={crumb.href} className="truncate transition-colors hover:text-ink">
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
      </nav>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          /*
           * Both actions used to be merchant routes unconditionally. A platform
           * admin who clicked either was redirected straight back to /admin, so
           * they read as dead icons — which is precisely what they were.
           */
          onClick={() => navigate(isPlatformAdmin ? '/admin/merchants?new=1' : '/payment-links?new=1')}
          /*
           * A payment link, not a payment: it is the only way to take money that
           * needs no code, so it is what "create" means to most merchants. It
           * used to point at /payments?new=1, which nothing handled.
           */
          title={isPlatformAdmin ? 'Add an organisation' : 'Create a payment link'}
          aria-label={isPlatformAdmin ? 'Add an organisation' : 'Create a payment link'}
          className="grid h-[34px] w-[34px] place-items-center rounded-md bg-accent text-white transition-colors hover:brightness-95"
        >
          <Plus className="h-4 w-4" />
        </button>

        <GlobalSearch />

        <Link
          to={isPlatformAdmin ? '/admin/health' : '/developers/webhooks'}
          title={isPlatformAdmin ? 'System health' : 'Webhook activity'}
          aria-label={isPlatformAdmin ? 'System health' : 'Webhook activity'}
          className="grid h-[34px] w-[34px] place-items-center rounded-md border text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink"
          style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
        >
          <Activity className="h-4 w-4" />
        </Link>

        <Clock />
        <ThemeToggle />
      </div>
    </div>
  );
}

export default TopBar;
